import React, { useEffect, useState } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'
import { Row, Col, Button, Collapse, Tooltip } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import isEmpty from 'lodash/isEmpty'
import groupBy from 'lodash/groupBy'
import map from 'lodash/map'
import find from 'lodash/find'

import Topology from '../SvgViews/Topology'
import ModalFullScreen from '../Modal/ModalFullScreen'
import ComponentFaultsHistoryList from '../ComponentFaultsHistoryList'
import { getElementIdField, getElementName } from '../../helpers/element'
import { getComponentFaultHistory } from '../../api/logging-api'

function SameIssuesHistory(props) {
  const {
    isOpen,
    onClose,
    components,
    componentCounts,
    componentFaultHistories,
    componentFaultHistoriesLoading,
    makeModelId,
    vin,
    vehicleTestResultId,
  } = props

  const [selectedComponentId, onComponentSelect] = useState(null)

  useEffect(() => {
    if (components.length === 0) {
      onComponentSelect(null)
      return
    }
    onComponentSelect((current) =>
      current == null
        ? components[0][getElementIdField(components[0])]
        : current,
    )
  }, [components])

  useEffect(() => {
    if (selectedComponentId && makeModelId && vin) {
      getComponentFaultHistory(
        makeModelId,
        vin,
        vehicleTestResultId,
        selectedComponentId,
      )
    }
  }, [selectedComponentId, makeModelId, vin, vehicleTestResultId])

  const selectedComponent = find(
    components,
    (component) =>
      component[getElementIdField(component)] === selectedComponentId,
  )

  const panels = map(components, (component) => {
    const id = component[getElementIdField(component)]
    const count = componentCounts[id] || 0
    const componentFaultsHistory = groupBy(
      componentFaultHistories[id] || [],
      'reason',
    )
    const componentFaultHistoryLoading = !!componentFaultHistoriesLoading[id]
    const componentName = getElementName(component)
    return {
      key: id,
      label: (
        <span className="same-issues-component-item">
          <span className="same-issues-component-name">{componentName}</span>
          {component.description && (
            <Tooltip
              title={component.description}
              overlayClassName="same-issues-component-description-tooltip"
            >
              <span className="same-issues-component-description">
                {component.description}
              </span>
            </Tooltip>
          )}
          <span className="same-issues-component-count">{count}</span>
        </span>
      ),
      children: (
        <ComponentFaultsHistoryList
          componentFaultsHistory={componentFaultsHistory}
          isLoading={componentFaultHistoryLoading}
        />
      ),
    }
  })

  const header = () => {
    return (
      <Row>
        <Col span={9}>
          <Row gap={2}>
            <Col span={5}>
              <Button icon={<ArrowLeftOutlined />} onClick={onClose}>
                <FormattedMessage id="generic.back" />
              </Button>
            </Col>
            <Col span={19} className="modal-title-container">
              <FormattedMessage id="tracer.sameIssues.modalHeader" />
            </Col>
          </Row>
        </Col>
      </Row>
    )
  }

  const content = () => {
    return (
      <Row className="same-issues-content-container">
        <Col span={9}>
          <div className="same-issues-component-list">
            {isEmpty(components) ? (
              <div className="same-issues-empty">
                <FormattedMessage id="tracer.componentHistory.noHistory" />
              </div>
            ) : (
              <Collapse
                accordion
                className="same-issues-collapse"
                items={panels}
                activeKey={selectedComponentId || []}
                onChange={(keys) => {
                  onComponentSelect(
                    Array.isArray(keys) ? keys[0] || null : keys || null,
                  )
                }}
              />
            )}
          </div>
        </Col>
        <Col span={15}>
          <Topology component={selectedComponent} />
        </Col>
      </Row>
    )
  }

  return (
    <ModalFullScreen
      HeaderProp={header}
      ContentProp={content}
      isOpen={isOpen}
      dataTestIdContainer={'same-issues-history-modal-container'}
      dataTestIdHeader={'same-issues-history-modal-header'}
      dataTestIdContent={'same-issues-history-modal-content'}
    />
  )
}

SameIssuesHistory.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  components: arrayOf(shape({})).isRequired,
  componentCounts: shape({}),
  componentFaultHistories: shape({}),
  componentFaultHistoriesLoading: shape({}),
  makeModelId: number,
  vin: string,
  vehicleTestResultId: number,
}

export default injectIntl(SameIssuesHistory)
